import { useEffect, useState, useRef, useCallback, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import RightModal from "../components/templates/RightModal";
import { getQuotationsList, selectQuotationsData, selectQuotationsLoading, deleteQuotation, getQuotationDetails, resetQuotationDetails } from "../redux/quotations/quotationsSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, selectRightModalData, setModal, setHasToRefresh } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import { useColorPalette } from '../hooks/useColorPalette';
import ListPagination from "../components/templates/ListPagination";

const AddEditQuotation = lazy(() => import("../components/quotationsPage/AddEditQuotation"));
const ViewQuotation = lazy(() => import("../components/quotationsPage/ViewQuotation"));

const QUOTATION_STATUSES = [
    { label: "Bozza", code: "bozza", color: "#6B7280" },
    { label: "Inviato", code: "inviato", color: "#3B82F6" },
    { label: "Accettato", code: "accettato", color: "#10B981" },
    { label: "Rifiutato", code: "rifiutato", color: "#EF4444" },
];

export default function QuotationsList() {
    const dispatch = useDispatch();
    const location = useLocation();
    const palette = useColorPalette();
    const quotationsList = useSelector(selectQuotationsData);
    const quotationsLoading = useSelector(selectQuotationsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [employeesList, setEmployeesList] = useState([]);
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    const tableRef = useRef(null);
    const prefillHandled = useRef(false);

    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalQuotationId = useSelector(selectRightModalId);
    const rightModalData = useSelector(selectRightModalData);

    // Intercetta prefill da navigazione (es. bottone "Crea Preventivo" da lead)
    useEffect(() => {
        if (location.state?.openNewQuotation && !prefillHandled.current) {
            prefillHandled.current = true;
            dispatch(setModal({
                mode: 'add',
                data: { prefillFromLead: location.state.prefillFromLead }
            }));
            window.history.replaceState({}, document.title);
        }
    }, [location.state, dispatch]);

    // Carica lista collaboratori per il filtro
    const loadEmployees = useCallback(async () => {
        if (!accountData?._id || !token) return;
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/users/getEmployeesList?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            setEmployeesList(Array.isArray(data) ? data : (data.data || []));
        } catch (err) {
            console.error('Errore caricamento collaboratori:', err);
        }
    }, [accountData, token]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    const handlePageUpdate = useCallback((start, length) => {
        if (!accountData?._id) return;
        dispatch(getQuotationsList({
            accountId: accountData._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            statusCode: statusFilter,
            userId: userFilter
        }));
        dispatch(resetRefreshNow());
    }, [dispatch, accountData, token, searchTerm, statusFilter, userFilter]);

    useEffect(() => {
        if (accountData?._id) {
            handlePageUpdate(0, 10);
            setDataTableKey(Date.now());
        }
    }, [accountData?._id, token, searchTerm, statusFilter, userFilter]);

    // Carica dettagli quando modal aperto
    useEffect(() => {
        if (rightModalIsOpen && rightModalQuotationId && (rightModalMode === 'edit' || rightModalMode === 'view')) {
            dispatch(getQuotationDetails({
                quotationId: rightModalQuotationId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalQuotationId, rightModalMode, dispatch, token]);

    // Reset dettagli quando modal chiuso
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetQuotationDetails());
        }
    }, [rightModalIsOpen, dispatch]);

    // Aggiorna tabella quando modal chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);

    // Refresh dati quando isRefreshNow
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);

    return (
        <div className="page">
            {quotationsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Preventivi" />

            <h1>Preventivi</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                            placeholder="Cerca preventivo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        {/* Filtro per stato */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Tutti gli stati</option>
                            {QUOTATION_STATUSES.map((status, index) => (
                                <option key={`status-filter-${index}`} value={status.code}>{status.label}</option>
                            ))}
                        </select>

                        {/* Filtro per collaboratore */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                        >
                            <option value="">Tutti i collaboratori</option>
                            {employeesList.map((emp) => (
                                <option key={emp._id} value={emp._id}>
                                    {[emp.name, emp.surname].filter(Boolean).join(' ')}
                                </option>
                            ))}
                        </select>

                        {/* Bottone Nuovo Preventivo */}
                        <button
                            className="primary"
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuovo Preventivo
                        </button>
                    </div>
                </div>

                {/* Lista preventivi */}
                <div className="w-full my-4" ref={tableRef}>
                    {quotationsLoading || !accountData?._id ? (
                        <LoadingSection />
                    ) : (
                        <ListPagination
                            initTotalElements={quotationsList?.recordsFiltered || 0}
                            onPageChange={handlePageUpdate}
                        >
                            <div className="w-full text-sm flex flex-col gap-2">
                                {/* Headers */}
                                <div className="w-full flex justify-between font-bold rounded-t-lg" style={{ backgroundColor: palette?.tableHeaderBg || '#f3f4f6', color: palette?.tableHeaderText || '#111827' }}>
                                    <div className="px-6 py-4 w-[10%]">CODICE</div>
                                    <div className="px-6 py-4 w-[18%]">CLIENTE</div>
                                    <div className="px-6 py-4 w-[15%]">IMPIANTO</div>
                                    <div className="px-6 py-4 w-[12%]">TOTALE</div>
                                    <div className="px-6 py-4 w-[10%]">STATO</div>
                                    <div className="px-6 py-4 w-[12%]">DATA</div>
                                    <div className="px-6 py-4 w-[10%]">ASSEGNATO</div>
                                    <div className="px-6 py-4 w-[13%] flex justify-end">AZIONI</div>
                                </div>

                                {/* Rows */}
                                {
                                    quotationsList?.data?.map((quotation, index) => {
                                        const isEven = index % 2 === 0;
                                        const rowBgColor = isEven ? (palette?.tableRowLight || '#ffffff') : (palette?.tableRowDark || '#f9fafb');
                                        const rowTextColor = isEven ? (palette?.textOnTableRowLight || '#111827') : (palette?.textOnTableRowDark || '#111827');

                                        return (
                                            <QuotationLine
                                                key={quotation._id}
                                                quotationData={quotation}
                                                index={index}
                                                rowBgColor={rowBgColor}
                                                rowTextColor={rowTextColor}
                                                rowHoverBgColor={palette?.highlightBg || '#e5e7eb'}
                                                rowHoverTextColor={palette?.textOnHighlight || '#111827'}
                                            />
                                        );
                                    })
                                }

                                {(!quotationsList?.data || quotationsList.data.length === 0) && !quotationsLoading && (
                                    <div className="w-full py-4 text-center text-gray-500">
                                        Nessun preventivo trovato
                                    </div>
                                )}
                            </div>
                        </ListPagination>
                    )}
                </div>
            </div>

            {/* RIGHT MODAL */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        <Suspense fallback={<LoadingSection />}>
                            {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                                <AddEditQuotation mode={rightModalMode} />
                            )}
                            {
                                rightModalMode === 'view' && rightModalQuotationId && (
                                    <ViewQuotation />
                                )
                            }
                        </Suspense>
                    </RightModal>
                )
            }
        </div>
    );
}

// Componente inline per la riga del preventivo
function QuotationLine({ quotationData, index, rowBgColor, rowTextColor, rowHoverBgColor, rowHoverTextColor }) {
    const [isHovered, setIsHovered] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const dispatch = useDispatch();
    const token = localStorage.getItem('installamelo-auth-token');

    const handleRowClick = () => {
        dispatch(setModal({
            mode: 'edit',
            id: quotationData._id
        }));
    };

    const handleViewClick = (e) => {
        e.stopPropagation();
        dispatch(setModal({
            mode: 'view',
            id: quotationData._id
        }));
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        dispatch(setModal({
            mode: 'edit',
            id: quotationData._id
        }));
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const confirmDelete = (e) => {
        e.stopPropagation();
        if (quotationData?._id) {
            dispatch(deleteQuotation({
                quotationId: quotationData._id,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    setShowDeleteConfirm(false);
                }
            });
        }
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '--';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDate = (date) => {
        if (!date) return '--';
        return new Date(date).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusBadge = () => {
        const color = quotationData?.statusColor || '#6B7280';
        return (
            <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: color + '20', color: color }}
            >
                {quotationData?.statusLabel || 'N/D'}
            </span>
        );
    };

    const getAssignedUsers = () => {
        if (!quotationData?.assignedUsers || quotationData.assignedUsers.length === 0) return '--';
        return (
            <div className="flex flex-wrap gap-1">
                {quotationData.assignedUsers.slice(0, 2).map((u, i) => (
                    <span
                        key={i}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                            backgroundColor: (u.userColor || '#6B7280') + '20',
                            color: u.userColor || '#6B7280'
                        }}
                    >
                        {u.userLabel}
                    </span>
                ))}
                {quotationData.assignedUsers.length > 2 && (
                    <span className="text-xs text-gray-500">+{quotationData.assignedUsers.length - 2}</span>
                )}
            </div>
        );
    };

    return (
        <>
            <div
                style={{
                    backgroundColor: isHovered ? rowHoverBgColor : rowBgColor,
                    color: isHovered ? rowHoverTextColor : rowTextColor,
                    transition: 'background-color 0.15s ease'
                }}
                className="w-full flex justify-between cursor-pointer rounded-lg border-b border-gray-100"
                onClick={handleRowClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="px-6 py-4 w-[10%] font-semibold">{quotationData?.code || '--'}</div>
                <div className="px-6 py-4 w-[18%] truncate">{quotationData?.customerLabel || '--'}</div>
                <div className="px-6 py-4 w-[15%] truncate">{quotationData?.systemLabel || '--'}</div>
                <div className="px-6 py-4 w-[12%] font-medium">{formatCurrency(quotationData?.totalWithVat)}</div>
                <div className="px-6 py-4 w-[10%]">{getStatusBadge()}</div>
                <div className="px-6 py-4 w-[12%]">{formatDate(quotationData?.quotationDate)}</div>
                <div className="px-6 py-4 w-[10%]">{getAssignedUsers()}</div>
                <div className="px-6 py-4 w-[13%] flex justify-end items-center space-x-2">
                    <button className="p-1 hover:bg-blue-100 rounded-full" onClick={handleViewClick} title="Visualizza">
                        <DynamicIcon iconClass="fa-solid fa-eye" />
                    </button>
                    <button className="p-1 hover:bg-blue-100 rounded-full" onClick={handleEditClick} title="Modifica">
                        <DynamicIcon iconClass="fa-solid fa-edit" />
                    </button>
                    <button className="p-1 hover:bg-red-100 rounded-full text-red-500" onClick={handleDeleteClick} title="Elimina">
                        <DynamicIcon iconClass="fa-solid fa-trash" />
                    </button>
                </div>
            </div>

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare il preventivo &quot;{quotationData?.code}&quot;?
                            Questa azione non può essere annullata.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button className="secondary" onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }}>
                                Annulla
                            </button>
                            <button className="danger" onClick={confirmDelete}>
                                <DynamicIcon iconClass="fas fa-trash" />
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
